import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import HomeSVG from "./HomeSVG";
import Filter from "./Filter";
import { ChevronLeftOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

const StoreCodeInput = ({ onStoreSelect, disabled, onInputChange }) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // Tracks dropdown visibility
  const containerRef = useRef(null);
  const inputTextFieldRef = useRef(null);

  const { storeCodes, isLoading,error } = useSelector((state) => state.gmbStore);
  const { gmbStoreCode } = useParams();

  // Effect to handle filter application
  useEffect(() => {
   
    if (isFilterApplied) {
      setSuggestions(storeCodes); // Show all stores when filter is applied
      setIsDropdownOpen(true); // Open the dropdown
      setHasSearched(true); // Display suggestions
      window.history.pushState({}, '', `/gmb`);// removing the store code from the url
    }
  }, [isFilterApplied, storeCodes]);

  // Close dropdown and rotate chevron when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setSuggestions([]);
        setHasSearched(false); // Close "No Stores found" message box
        setIsDropdownOpen(false); // Rotate chevron back
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if(gmbStoreCode){

      const filteredStoreCode =
      storeCodes?.filter(
        (store) =>
          store.store_code == gmbStoreCode
      )||[] ;
      if(filteredStoreCode.length>0){
        setInputValue(`${filteredStoreCode[0]?.store_code} - ${filteredStoreCode[0]?.loc_name}`);
      }
    }
  },[gmbStoreCode,storeCodes])

  const handleKeyUp = (event) => {
    const value = event.target.value;
    setInputValue(value);
    setHasInteracted(true);
    if (onInputChange) {
      onInputChange();
    }

    if (value) {
      setHasSearched(true);
      setIsDropdownOpen(true); // Open dropdown
      const filteredSuggestions =
        storeCodes?.filter(
          (store) =>
            store.store_code.toLowerCase().includes(value.toLowerCase()) ||
            store.loc_name.toLowerCase().includes(value.toLowerCase())
        ) || [];
      setSuggestions(filteredSuggestions);
    } else if (!isFilterApplied) {
      setHasSearched(false);
      setSuggestions([]);
      setIsDropdownOpen(false); // Close dropdown
    }
  };

  const handleSuggestionClick = (store_code, loc_name) => {
    setHasSearched(false);
    setInputValue(`${store_code} - ${loc_name}`);
    setSuggestions([]);
    setIsDropdownOpen(false); // Close dropdown
    onStoreSelect({ store_code, loc_name });
  };

  const handleFocus = (e) => {
    e.target.select();
    setSuggestions(storeCodes); // Populate suggestions with all available store codes
    setHasSearched(true); // Ensure "No Stores found" message is displayed
    setIsDropdownOpen(true); // Open the dropdown
  };

  return (
    <div className="w-[100%] relative" ref={containerRef}>
      <div className="flex flex-row items-center justify-center gap-36">
        <TextField
          id="store-code"
          autoComplete="off"
          placeholder="Enter a store code"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyUp={handleKeyUp}
          onFocus={handleFocus}
          disabled={disabled}
          fullWidth
          inputRef={inputTextFieldRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <div className="bg-white rounded-xl w-fit h-fit p-1.5 ">
                  <HomeSVG />
                </div>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isLoading && hasInteracted && (
                  <CircularProgress
                    size={24}
                    style={{
                      color: "#EC221F",
                      position: "absolute",
                      right: "8px",
                    }}
                  />
                )}
                {isFilterApplied && !isLoading && (
                  <ChevronLeftOutlined
                    className={`transition-transform duration-200 ${
                      isDropdownOpen ? "rotate-90" : "-rotate-90"
                    }`}
                  />
                )}
              </InputAdornment>
            ),
            sx: {
              backgroundColor: "#F1F6FA",
              paddingLeft: "8px",
              color: "black",
              fontSize: "12px",
              borderRadius: "8px",
              height: "48px",
              marginLeft: "0px",
            },
          }}
          sx={{
            input: { cursor: "pointer" },
            ".MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "transparent", // Remove default border
              },
              "&.Mui-focused fieldset": {
                borderColor: "black", // Focused border color
              },
            },
          }}
        />
        <Filter handleFilterApplied={(value) => {
           if (inputTextFieldRef.current && isFilterApplied) {
            inputTextFieldRef.current.value = ""; 
            setInputValue("");
          } setInputValue("");
         setIsFilterApplied(value)
        }} />
      </div>

      {/* Suggestions List */}
      {storeCodes && (
        <Box
          className={`absolute ${
            suggestions?.length ? "block" : "hidden"
          }  w-[40%]  max-h-60 overflow-y-auto bg-white rounded-b-lg shadow-lg z-10`}
        >
          <ul className="list-none p-0 m-0 w-[100%]">
            {suggestions?.map((suggestion, index) => (
              <li
                key={index}
                className="p-2 text-gray-800 rounded-lg cursor-pointer hover:bg-gray-200"
                onClick={() =>
                  handleSuggestionClick(
                    suggestion.store_code,
                    suggestion.loc_name
                  )
                }
              >
                <div className="flex flex-row items-center justify-between gap-10">
                  <Typography>
                    {suggestion.store_code} - {suggestion.loc_name}
                  </Typography>
                </div>
              </li>
            ))}
          </ul>
        </Box>
      )}

      {/* No Results Message */}
      {!isLoading && hasSearched && suggestions?.length === 0 && (
        <Box className="absolute block w-[40%]  max-h-60 overflow-y-auto bg-white rounded-b-lg shadow-lg z-10">
          <ul className="list-none p-0 m-0 w-[100%]">
            <li className="p-2 text-gray-800 rounded-lg cursor-pointer hover:bg-gray-200">
              <Typography>No Stores found.</Typography>
            </li>
          </ul>
        </Box>
      )}
    </div>
  );
};

export default StoreCodeInput;
